/**
 * Module 'ai': the foundation for AI agents that monitor and manage Orgus. Every
 * capability an agent may use is declared once here, typed, with its risk tier and a JSON
 * Schema of its input. The MCP server (tools/orgus-mcp), the HTTP API
 * (GET /api/ai/capabilities, POST /api/ai/invoke) and the hub's gate all read this
 * catalogue. Agents act through the same audited, policy-gated paths as people (actor kind
 * 'agent', audited as agent:<name>). See docs/architecture/ai-native.md and ADR 0003.
 *
 * Browser-safe and dependency-free: the web console renders the catalogue too.
 */
import type { ActionKind, Id, Ts } from '../index.ts';
import { settingByKey } from './catalog-areas.ts';
import { BODY_MAX, CRITERIA_MAX, CRITERION_MAX, DEFECT_FIELD_MAX, ESTIMATE_MAX, ITEM_KINDS, ITEM_STATUSES, LABELS_MAX, LABEL_MAX, LINK_TYPES, SEVERITIES, TITLE_MAX } from './missions.ts';

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

/**
 * How much harm a capability can do (architecture gap analysis 11.5).
 *   read      look, never change anything
 *   operate   everyday changes that are easy to undo: start or stop a session, pause a pipeline
 *   elevated  changes that destroy work or widen what runs unattended
 *   critical  equivalent to a shell on a machine, or a change to policy: a person must do it
 */
export type CapabilityTier = 'read' | 'operate' | 'elevated' | 'critical';
export const CAPABILITY_TIERS: readonly CapabilityTier[] = ['read', 'operate', 'elevated', 'critical'];

export const TIER_LABEL: Record<CapabilityTier, string> = {
  read: 'Read',
  operate: 'Operate',
  elevated: 'Elevated',
  critical: 'Critical',
};

export const TIER_DESCRIPTION: Record<CapabilityTier, string> = {
  read: 'Looks at state, transcripts, costs and the audit log. Changes nothing.',
  operate: 'Everyday changes that are easy to undo: start or stop a session, pause a pipeline, deny an approval.',
  elevated: 'Changes that remove work or widen what runs unattended. Agents need a token scope that names them.',
  critical: 'As powerful as a shell on the machine, or a change to policy. Only a person can do these.',
};

export function tierRank(t: CapabilityTier): number {
  return CAPABILITY_TIERS.indexOf(t);
}

export function isCapabilityTier(v: unknown): v is CapabilityTier {
  return typeof v === 'string' && (CAPABILITY_TIERS as readonly string[]).includes(v);
}

/** The higher of two tiers. */
export function maxTier(a: CapabilityTier, b: CapabilityTier): CapabilityTier {
  return tierRank(a) >= tierRank(b) ? a : b;
}

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

/** The subset of JSON Schema the catalogue uses (and the hub validates). */
export interface JsonSchema {
  type?: 'object' | 'string' | 'number' | 'integer' | 'boolean' | 'array';
  description?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  additionalProperties?: boolean;
  enum?: readonly (string | number | boolean)[];
  items?: JsonSchema;
  maxItems?: number;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  default?: unknown;
}

/**
 * Where a capability is carried out.
 *   read     answered by the hub from its own state (or, for transcripts, the machine's agent)
 *   action   an Orgus action: POST /api/actions through the normal gates and audit
 *   module   a feature module's own route (approvals, missions), called as the same caller
 *   api      any feature module's route by method and path (api.get, api.call), called as
 *            the same caller; the tier comes from the method and the path (apiCallTier)
 */
export type CapabilityKind = 'read' | 'action' | 'module' | 'api';

/** A condition on the input that raises the tier, e.g. a session started with permission checks off. */
export interface TierEscalation {
  /** Input field and the values that raise the tier. */
  field: string;
  values: readonly (string | number | boolean)[];
  tier: CapabilityTier;
  /** Why, in one line. */
  reason: string;
}

export interface Capability {
  /** Stable name, e.g. 'sessions.list', 'session.stop'. The MCP tool name is mcpToolName(name). */
  name: string;
  title: string;
  description: string;
  tier: CapabilityTier;
  kind: CapabilityKind;
  /** For kind 'action': the action it runs. */
  action?: ActionKind;
  /** For kind 'module': the hub module that owns it. */
  module?: string;
  /** JSON Schema of the input (always an object). */
  input: JsonSchema;
  /** Removes or stops something. */
  destructive?: boolean;
  escalations?: readonly TierEscalation[];
  /** For a routed module capability (AI_ROUTED): the module route it calls, as the console does. */
  route?: CapabilityRoute;
  /** Above operate: why, in one line, where a person grants it (the token dialog). */
  risk?: string;
}

/** A module route a capability calls; ':name' path parameters come from the input. */
export interface CapabilityRoute {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  /** Fixed body fields the console sends too (e.g. a confirmation). */
  body?: Record<string, unknown>;
}

/** Bumped whenever a capability is added, removed or changes tier. */
export const AI_CATALOGUE_VERSION = '2026-11-01.1';

const id = (description: string): JsonSchema => ({ type: 'string', minLength: 1, maxLength: 200, description });
const limit = (max: number, def: number): JsonSchema => ({ type: 'integer', minimum: 1, maximum: max, default: def, description: `How many to return, newest first (default ${def}, at most ${max}).` });
const obj = (properties: Record<string, JsonSchema>, required: string[] = []): JsonSchema => ({
  type: 'object',
  properties,
  ...(required.length ? { required } : {}),
  additionalProperties: false,
});
const machineHint = id('The machine to run on, by id or name. Optional when Orgus can tell from the other fields.');

/** A backlog item's fields, as item.create and item.update take them (ItemInput in ./missions.ts). */
const TITLE: JsonSchema = { type: 'string', minLength: 1, maxLength: TITLE_MAX, description: 'A short title.' };
const words = (what: string): JsonSchema => ({ type: 'array', maxItems: LABELS_MAX, items: { type: 'string', minLength: 1, maxLength: LABEL_MAX }, description: what });
const defectText = (what: string): JsonSchema => ({ type: 'string', maxLength: DEFECT_FIELD_MAX, description: what });
const ITEM_FIELDS: Record<string, JsonSchema> = {
  body: { type: 'string', maxLength: BODY_MAX, description: 'The description, in Markdown.' },
  status: { type: 'string', enum: ITEM_STATUSES, description: 'Where it is. Default: triage, or backlog for an epic.' },
  priority: { type: 'integer', enum: [0, 1, 2, 3], description: '0 urgent, 1 high, 2 normal, 3 low.' },
  owner: { type: 'string', minLength: 1, maxLength: 100, description: 'Who owns it.' },
  parentId: id('The epic or feature it belongs to (itm_...).'),
  estimate: { type: 'number', minimum: 0, maximum: ESTIMATE_MAX, description: 'Points.' },
  labels: words('Labels.'),
  acceptance: {
    type: 'array',
    maxItems: CRITERIA_MAX,
    description: 'Acceptance criteria, in order. On update this replaces the list; keep each id to keep that criterion and its check.',
    items: obj({
      id: id('Keep this existing criterion (update only).'),
      text: { type: 'string', maxLength: CRITERION_MAX, description: 'The criterion, or the Then line of a Given/When/Then.' },
      given: { type: 'string', maxLength: CRITERION_MAX },
      when: { type: 'string', maxLength: CRITERION_MAX },
      then: { type: 'string', maxLength: CRITERION_MAX },
    }),
  },
  defect: {
    ...obj({
      severity: { type: 'string', enum: SEVERITIES },
      environment: defectText('Where it happens.'),
      steps: defectText('Steps to reproduce.'),
      expected: defectText('What should happen.'),
      actual: defectText('What happens.'),
    }),
    description: 'For a defect: severity and how to reproduce it.',
  },
};

/** Every read an agent can make. */
export const AI_READS: readonly Capability[] = [
  {
    name: 'state.summary',
    title: 'Summary of everything',
    description: 'One snapshot: sessions by status, running subagents and workflows, pipeline items in flight, machines online, jobs, what needs a person, spend today, pending approvals and running missions.',
    tier: 'read',
    kind: 'read',
    input: obj({}),
  },
  {
    name: 'needs_you.list',
    title: 'What needs a person',
    description: 'Sessions waiting for an answer, pending approvals, failed pipeline items in the last day, machines that stopped reporting, and dirty worktrees nobody has worked in for a day. Most urgent first.',
    tier: 'read',
    kind: 'read',
    input: obj({}),
  },
  {
    name: 'sessions.list',
    title: 'List sessions',
    description: 'Claude Code sessions across every machine, most recently active first, with status, title, model, tokens, subagents and workflows.',
    tier: 'read',
    kind: 'read',
    input: obj({
      status: { type: 'string', enum: ['working', 'waiting', 'idle', 'ended', 'live'], description: "Only this status. 'live' means anything but ended." },
      projectId: id('Only sessions in this project (id or name).'),
      machineId: id('Only sessions on this machine (id or name).'),
      limit: limit(200, 50),
    }),
  },
  {
    name: 'session.get',
    title: 'Get one session',
    description: 'Everything Orgus knows about one session, including its subagents and workflow runs.',
    tier: 'read',
    kind: 'read',
    input: obj({ sessionId: id('The session id.') }, ['sessionId']),
  },
  {
    name: 'session.transcript',
    title: 'Read a session transcript',
    description: 'The latest entries of a session transcript: prompts, assistant text, tool calls and results, read from the machine it runs on.',
    tier: 'read',
    kind: 'read',
    input: obj({ sessionId: id('The session id.'), limit: limit(500, 100) }, ['sessionId']),
  },
  {
    name: 'pipelines.list',
    title: 'List pipelines',
    description: 'Every project delivery pipeline: stages, items in flight, recent results, capacity and the actions it supports.',
    tier: 'read',
    kind: 'read',
    input: obj({ projectId: id('Only this project (id or name).') }),
  },
  {
    name: 'projects.list',
    title: 'List projects',
    description: 'Projects across machines, each with its checkouts and worktrees (branch, ahead and behind, uncommitted changes).',
    tier: 'read',
    kind: 'read',
    input: obj({}),
  },
  {
    name: 'machines.list',
    title: 'List machines',
    description: 'Machines with their liveness (online, stale or offline), CPU, memory and load, and how many sessions and jobs each runs.',
    tier: 'read',
    kind: 'read',
    input: obj({}),
  },
  {
    name: 'machines.load',
    title: 'What is using each machine',
    description:
      "Why a machine is busy and how much room it has: the CPU breakdown by heavy work (with its work lease), session, project and background (antivirus, indexer, editors), the work budget's leases and waiters, free slots, and how many more heavy commands fit now. Use it for 'why is my CPU high'; for 'how many more agents can I run' use machines.room. Needs the machine-load module.",
    tier: 'read',
    kind: 'read',
    module: 'machine-load',
    input: obj({ machineId: id('Only this machine (id or name).') }),
  },
  {
    name: 'machines.room',
    title: 'Where a session has room',
    description:
      "Whether each machine takes one more session now and why, best first: has room, nearly full, full or not taking work, one plain line ('12 of 18 slots in use, 1 waiting', 'Memory 92% used'), how many more sessions fit, and whether it has a checkout of the project. Room is heavy-work headroom and memory, not session count; a machine without a load report is judged by session count. Use it for 'how many more agents can I run here' and 'where should this run'. Needs the scheduler module.",
    tier: 'read',
    kind: 'read',
    module: 'scheduler',
    input: obj({ projectId: id('Rank for this project (id or name): machines with a checkout of it first.'), machineId: id('Only this machine (id or name).') }),
  },
  {
    name: 'jobs.list',
    title: 'List jobs',
    description: 'Commands run on machines through Orgus, newest first.',
    tier: 'read',
    kind: 'read',
    input: obj({ machineId: id('Only this machine (id or name).'), limit: limit(200, 50) }),
  },
  {
    name: 'costs.summary',
    title: 'Spend and budgets',
    description: 'Spend today and this month, budgets and how close each is, and the costliest sessions. Needs the costs module.',
    tier: 'read',
    kind: 'read',
    module: 'costs',
    input: obj({ limit: limit(100, 10) }),
  },
  {
    name: 'approvals.list',
    title: 'List approvals',
    description: 'Requests from launched sessions waiting for a decision, and recent decisions. Needs the approvals module.',
    tier: 'read',
    kind: 'read',
    module: 'approvals',
    input: obj({ state: { type: 'string', enum: ['pending', 'recent', 'all'], default: 'pending', description: 'Pending only (default), recent decisions, or both.' } }),
  },
  {
    name: 'missions.list',
    title: 'List missions',
    description: 'Missions and the backlog items behind them, with their state and sessions. Needs the missions module.',
    tier: 'read',
    kind: 'read',
    module: 'missions',
    input: obj({ projectId: id('Only this project (id or name).'), state: { type: 'string', enum: ['planned', 'running', 'waiting', 'done', 'failed', 'stopped'] } }),
  },
  {
    name: 'audit.list',
    title: 'Read the audit log',
    description: 'Every action taken through Orgus, by whom, on what, and the result. Newest first.',
    tier: 'read',
    kind: 'read',
    input: obj({
      actor: id("Only this actor, e.g. 'owner' or 'agent:ops'."),
      action: id('Only this action kind.'),
      machineId: id('Only this machine (id or name).'),
      result: { type: 'string', enum: ['ok', 'error'] },
      limit: limit(100, 50),
    }),
  },
  {
    name: 'feed.list',
    title: 'Read the activity feed',
    description: 'What happened recently: sessions starting and ending, subagents, workflows, pipeline items, machines, actions and jobs. Newest first.',
    tier: 'read',
    kind: 'read',
    input: obj({ kind: id("Only this kind, e.g. 'session.waiting'."), projectId: id('Only this project (id or name).'), machineId: id('Only this machine (id or name).'), limit: limit(200, 50) }),
  },
];

const BYPASS: TierEscalation = {
  field: 'permissionMode',
  values: ['bypassPermissions'],
  tier: 'elevated',
  reason: 'A session with permission checks off runs any tool without asking.',
};

/** Every change an agent can ask for. */
export const AI_ACTIONS: readonly Capability[] = [
  {
    name: 'session.start',
    title: 'Start a session',
    description: 'Start Claude Code with a prompt in a checkout (or a new worktree) on a machine, headless or in a terminal window.',
    tier: 'operate',
    kind: 'action',
    action: 'session.start',
    escalations: [BYPASS],
    input: obj(
      {
        machineId: machineHint,
        checkoutId: id('The checkout to run in. Give this or cwd.'),
        cwd: { type: 'string', minLength: 1, maxLength: 1000, description: 'A folder on the machine. Give this or checkoutId.' },
        prompt: { type: 'string', minLength: 1, maxLength: 20000, description: 'The first prompt.' },
        mode: { type: 'string', enum: ['headless', 'terminal'], default: 'headless', description: 'headless runs in the background; terminal opens a window.' },
        permissionMode: { type: 'string', enum: ['default', 'acceptEdits', 'plan', 'bypassPermissions'], description: 'Claude Code permission mode. bypassPermissions raises the tier to elevated.' },
        model: { type: 'string', minLength: 1, maxLength: 100, description: 'Model id or alias.' },
        newWorktreeBranch: { type: 'string', minLength: 1, maxLength: 200, description: 'Create a new worktree on this branch and run there.' },
      },
      ['prompt'],
    ),
  },
  {
    name: 'session.stop',
    title: 'Stop a session',
    description: "Stop a session's process tree. Its transcript stays.",
    tier: 'operate',
    kind: 'action',
    action: 'session.stop',
    destructive: true,
    input: obj({ sessionId: id('The session to stop.'), machineId: machineHint }, ['sessionId']),
  },
  {
    name: 'job.run',
    risk: 'As powerful as a shell on that machine.',
    title: 'Run a command',
    description: 'Run a shell command in a checkout. As powerful as a shell on that machine, so only a person may run it.',
    tier: 'critical',
    kind: 'action',
    action: 'job.run',
    input: obj(
      {
        machineId: machineHint,
        checkoutId: id('The checkout to run in. Give this or cwd.'),
        cwd: { type: 'string', minLength: 1, maxLength: 1000, description: 'A folder on the machine. Give this or checkoutId.' },
        command: { type: 'string', minLength: 1, maxLength: 4000, description: 'The command line.' },
      },
      ['command'],
    ),
  },
  {
    name: 'job.cancel',
    title: 'Cancel a job',
    description: 'Stop a running command started through Orgus.',
    tier: 'operate',
    kind: 'action',
    action: 'job.cancel',
    destructive: true,
    input: obj({ jobId: id('The job to cancel.'), machineId: machineHint }, ['jobId']),
  },
  {
    name: 'worktree.remove',
    risk: 'Deletes a worktree folder from disk.',
    title: 'Remove a worktree',
    description: 'Remove a git worktree. Refused when it has uncommitted changes or a live session works in it.',
    tier: 'elevated',
    kind: 'action',
    action: 'worktree.remove',
    destructive: true,
    input: obj({ checkoutId: id('The worktree checkout to remove.'), machineId: machineHint }, ['checkoutId']),
  },
  {
    name: 'worktree.integrate',
    risk: 'Commits in a worktree, merges main into it and lands it on main.',
    title: 'Merge main and land a mission worktree',
    description: "Commit a mission worktree's changes, merge main into its branch (a conflict is aborted) and land it with the project's flow.land, else its tests and a fast-forward of a clean main checkout. Refused for a main checkout, a live session in it or another branch.",
    tier: 'elevated',
    kind: 'action',
    action: 'worktree.integrate',
    destructive: true,
    input: obj({ checkoutId: id('The mission worktree checkout.'), branch: id('The branch the worktree must be on.'), missionId: id('The mission the work belongs to.'), title: id('The mission title, for the commit message.'), machineId: machineHint }, ['checkoutId', 'branch', 'missionId', 'title']),
  },
  {
    name: 'worktree.landed',
    title: 'Check whether a branch landed',
    description: "Ask a machine whether a branch tip is on the project's main branch and whether the branch still exists. Read-only: nothing is fetched or changed.",
    tier: 'operate',
    kind: 'action',
    action: 'worktree.landed',
    input: obj({ projectId: id('The project.'), sha: id('The branch tip to look for on main.'), branch: id('The branch.'), machineId: machineHint }, ['projectId', 'sha', 'branch']),
  },
  {
    name: 'pipeline.pause',
    title: 'Pause a pipeline',
    description: "Pause a project's pipeline: nothing new starts until it is resumed.",
    tier: 'operate',
    kind: 'action',
    action: 'pipeline.pause',
    input: obj({ projectId: id('The project, by id or name.'), machineId: machineHint }, ['projectId']),
  },
  {
    name: 'pipeline.resume',
    title: 'Resume a pipeline',
    description: "Resume a paused project's pipeline.",
    tier: 'operate',
    kind: 'action',
    action: 'pipeline.resume',
    input: obj({ projectId: id('The project, by id or name.'), machineId: machineHint }, ['projectId']),
  },
  {
    name: 'pipeline.restart',
    risk: 'Work in flight may be retried or lost.',
    title: 'Restart a pipeline',
    description: "Restart a project's pipeline runner. Work in flight may be retried.",
    tier: 'elevated',
    kind: 'action',
    action: 'pipeline.restart',
    destructive: true,
    input: obj({ projectId: id('The project, by id or name.'), machineId: machineHint }, ['projectId']),
  },
  {
    name: 'project.add',
    title: 'Watch a project',
    description: 'Start watching a git checkout on a machine. Orgus only reads it.',
    tier: 'operate',
    kind: 'action',
    action: 'project.add',
    input: obj({ machineId: id('The machine the checkout is on, by id or name.'), path: { type: 'string', minLength: 1, maxLength: 1000, description: 'The checkout folder.' } }, ['machineId', 'path']),
  },
  {
    name: 'project.remove',
    title: 'Stop watching a project',
    description: 'Stop watching a project on a machine. Nothing in the repo is touched.',
    tier: 'operate',
    kind: 'action',
    action: 'project.remove',
    destructive: true,
    input: obj({ projectId: id('The project, by id or name.'), machineId: id('The machine, by id or name.') }, ['projectId', 'machineId']),
  },
  {
    name: 'approval.approve',
    risk: 'Lets a launched session do what it asked to.',
    title: 'Approve a request',
    description: "Allow a launched session's pending request. Allowing for the whole project writes a policy rule, which only a person may do.",
    tier: 'elevated',
    kind: 'module',
    module: 'approvals',
    escalations: [{ field: 'scope', values: ['project'], tier: 'critical', reason: 'A project-wide allow writes a policy rule.' }],
    input: obj(
      {
        approvalId: id('The approval request id (apr_...).'),
        scope: { type: 'string', enum: ['once', 'session', 'project'], default: 'once', description: 'How far the allow reaches.' },
        note: { type: 'string', maxLength: 1000, description: 'Why, for the audit log.' },
      },
      ['approvalId'],
    ),
  },
  {
    name: 'approval.deny',
    title: 'Deny a request',
    description: "Deny a launched session's pending request. Always safe: the session is told no.",
    tier: 'operate',
    kind: 'module',
    module: 'approvals',
    input: obj({ approvalId: id('The approval request id (apr_...).'), note: { type: 'string', maxLength: 1000, description: 'Why, shown to the session and in the audit log.' } }, ['approvalId']),
  },
  {
    name: 'mission.start',
    title: 'Start a mission',
    description: 'Start a mission in a project: a goal that Orgus runs as one or more sessions. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj(
      {
        projectId: id('The project, by id or name.'),
        title: { type: 'string', minLength: 1, maxLength: 200, description: 'A short title.' },
        goal: { type: 'string', minLength: 1, maxLength: 20000, description: 'What done looks like.' },
        itemId: id('The backlog item it works on, if any.'),
        machineId: id('Where to run. Optional: the online machine with the most room that has a checkout of the project.'),
        when: {
          type: 'string',
          enum: ['now', 'room'],
          default: 'now',
          description: 'now: start at once, even on a full machine. room: queue it so it starts when a machine has room (pinned to machineId when given). Check machines.room first, and let the person choose when the machine is full.',
        },
        budget: { type: 'number', minimum: 0, maximum: 10000, description: 'USD budget for the whole mission.' },
      },
      ['projectId', 'title', 'goal'],
    ),
  },
  {
    name: 'item.create',
    title: 'Create a backlog item',
    description: 'Create an epic, feature, story, task, defect, spike or chore in a project, with its acceptance criteria. Answers the item with its id and key. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj({ projectId: id('The project, by id or name.'), kind: { type: 'string', enum: ITEM_KINDS, description: 'What sort of item.' }, title: TITLE, ...ITEM_FIELDS }, ['projectId', 'kind', 'title']),
  },
  {
    name: 'item.update',
    title: 'Update a backlog item',
    description:
      'Change a backlog item: its status, fields or acceptance criteria, and check or uncheck criteria. While a mission runs the item, a status change needs statusReason. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj(
      {
        itemId: id('The item id (itm_...).'),
        kind: { type: 'string', enum: ITEM_KINDS, description: 'Change what sort of item it is.' },
        title: TITLE,
        ...ITEM_FIELDS,
        statusReason: { type: 'string', minLength: 1, maxLength: 500, description: 'Why, when changing the status a running mission set. Audited, and the status stays until the next start.' },
        criteria: {
          type: 'array',
          maxItems: CRITERIA_MAX,
          description: 'Check or uncheck acceptance criteria by id, after the other changes.',
          items: obj({ id: id('The criterion id.'), done: { type: 'boolean', description: 'Met (true) or not (false).' } }, ['id', 'done']),
        },
      },
      ['itemId'],
    ),
  },
  {
    name: 'item.link',
    title: 'Link two backlog items',
    description: 'Link two items in the same project: blocks, relates, duplicates, implements or affects. Linking twice answers the existing link. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj(
      {
        type: { type: 'string', enum: LINK_TYPES, description: 'How from relates to to: from blocks to, from implements to, and so on.' },
        from: id('The item the link starts at.'),
        to: id('The item the link points at.'),
        inverse: { type: 'boolean', description: 'Read it from the other end: from is blocked by to.' },
      },
      ['type', 'from', 'to'],
    ),
  },
  {
    name: 'item.import',
    title: 'Import a document into the backlog',
    description:
      'Start the Import a document AI job on a text or an uploaded file. It proposes epics, stories and criteria; nothing is created until proposal.accept. Waits for the job (waitSeconds) and answers the proposal, or the job to look at later with api.get /api/aijobs/<id>. Values that look like secrets are masked first. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj(
      {
        projectId: id('The project, by id or name.'),
        text: { type: 'string', minLength: 1, maxLength: 400_000, description: 'The document text. Give this or attachmentId.' },
        name: { type: 'string', minLength: 1, maxLength: 120, description: 'What to call the document (default Pasted text).' },
        attachmentId: id('An uploaded file to read (att_...). Give this or text.'),
        guidance: { type: 'string', maxLength: 2000, description: 'What to focus on, for the AI.' },
        parentId: id('An epic or feature the proposed items go under.'),
        model: { type: 'string', minLength: 1, maxLength: 100, description: 'Model id or alias for the job.' },
        machineId: id('Where the job runs. Optional: the hub picks one.'),
        waitSeconds: { type: 'integer', minimum: 0, maximum: 600, default: 120, description: 'How long to wait for the proposal (default 120, 0 to answer at once).' },
      },
      ['projectId'],
    ),
  },
  {
    name: 'proposal.accept',
    title: 'Accept a proposal',
    description:
      'Apply an open AI proposal (an import, split, draft or defect): create its items, make its edits and links. With refs, only those proposed items are created and the rest are rejected. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    input: obj(
      {
        proposalId: id('The proposal id (prp_...).'),
        refs: { type: 'array', maxItems: 300, items: id('A proposed item ref.'), description: 'Only these proposed items. Default: every item, edit and link.' },
      },
      ['proposalId'],
    ),
  },
  {
    name: 'proposal.discard',
    title: 'Discard a proposal',
    description: 'Close an open AI proposal without creating anything. Needs the missions module.',
    tier: 'operate',
    kind: 'module',
    module: 'missions',
    destructive: true,
    input: obj({ proposalId: id('The proposal id (prp_...).') }, ['proposalId']),
  },
];

/**
 * Everyday console actions by their module route (kind 'module' with a `route`): each host runs
 * them the same way (routeCall), as the same caller, through that route's own role, scope and
 * personal-machine checks and audit. Path parameters (':name') come from the input; the rest of
 * the input is the query (GET) or the body.
 */
const reason: JsonSchema = { type: 'string', minLength: 1, maxLength: 500, description: 'Why, for the audit log.' };
const routed = (c: Omit<Capability, 'kind'> & { route: CapabilityRoute; module: string }): Capability => ({ ...c, kind: 'module' });
export const AI_ROUTED: readonly Capability[] = [
  // Reads a person sees in the console.
  routed({ name: 'session.messages', title: 'Read messages sent to a session', description: 'The messages people and agents sent to a running session from Orgus, oldest first.', tier: 'read', module: 'session-control', route: { method: 'GET', path: '/api/sessions/:sessionId/messages' }, input: obj({ sessionId: id('The session id.') }, ['sessionId']) }),
  routed({ name: 'cloud.vms.list', title: 'List cloud VMs', description: 'The cloud connection and every VM Orgus created in it: state (running, stopped, starting), size, region, hourly price and the machine it runs.', tier: 'read', module: 'cloud', route: { method: 'GET', path: '/api/cloud' }, input: obj({}) }),
  routed({ name: 'landing.list', title: 'List landings', description: 'The landing queues: what waits to reach main, what is checking, what failed and why, and each environment.', tier: 'read', module: 'landing', route: { method: 'GET', path: '/api/landing' }, input: obj({}) }),
  routed({ name: 'watch.list', title: 'List incidents', description: 'Open incidents Orgus watches for (red main, stuck landings, stale worktrees, machines in trouble) and who is on each.', tier: 'read', module: 'watch', route: { method: 'GET', path: '/api/watch' }, input: obj({}) }),
  routed({ name: 'notify.inbox', title: 'Read the notification inbox', description: "The caller's notifications, newest first, with what is unread.", tier: 'read', module: 'notify', route: { method: 'GET', path: '/api/notify/inbox' }, input: obj({}) }),
  routed({ name: 'secrets.list', title: 'List secrets', description: 'Secret names, scopes, descriptions and leases. Never a value: no route returns one.', tier: 'read', module: 'secrets', route: { method: 'GET', path: '/api/secrets' }, input: obj({}) }),
  routed({ name: 'access.list', title: 'List roles and grants', description: 'Roles, who holds which at the org, a workspace, a project or an environment, and approval policies. With nothing saved, every member role shows as an org grant.', tier: 'read', module: 'rbac', route: { method: 'GET', path: '/api/rbac' }, input: obj({}) }),
  routed({ name: 'teams.list', title: 'List teams and assignments', description: 'Workspaces, teams, ownership, routing, assignments and who is present.', tier: 'read', module: 'teams', route: { method: 'GET', path: '/api/teams' }, input: obj({}) }),
  routed({ name: 'templates.list', title: 'List templates', description: 'Saved session and mission templates people start work from.', tier: 'read', module: 'templates', route: { method: 'GET', path: '/api/templates' }, input: obj({}) }),
  routed({ name: 'flows.list', title: 'List flows', description: 'Saved workflows (flows) and their revisions.', tier: 'read', module: 'flows', route: { method: 'GET', path: '/api/flows' }, input: obj({}) }),
  routed({ name: 'policy.effective', title: 'Read the effective policy', description: 'The rules that apply now, after freezes and overrides, and what each one allows or asks.', tier: 'read', module: 'policy', route: { method: 'GET', path: '/api/policy/effective' }, input: obj({}) }),
  routed({ name: 'terminals.list', title: 'List terminals', description: "Terminals open on machines, who opened them and who watches. Typing into one stays a person's.", tier: 'read', module: 'term', route: { method: 'GET', path: '/api/term' }, input: obj({}) }),
  routed({ name: 'finance.report', title: 'Spend report', description: 'Spend by period, project, person, model and account, with subscriptions and manual entries. Needs the finance module.', tier: 'read', module: 'finance', route: { method: 'GET', path: '/api/finance/report' }, input: obj({}) }),
  routed({ name: 'aijobs.list', title: 'List AI jobs', description: 'AI jobs (imports, splits, drafts) with their state and result, newest first.', tier: 'read', module: 'aijobs', route: { method: 'GET', path: '/api/aijobs' }, input: obj({}) }),
  // Everyday changes, easy to undo.
  routed({ name: 'session.message', title: 'Message a session', description: 'Send a message to a running session; it reads it at its next turn.', tier: 'operate', module: 'session-control', route: { method: 'POST', path: '/api/sessions/:sessionId/messages' }, input: obj({ sessionId: id('The session id.'), text: { type: 'string', minLength: 1, maxLength: 20000, description: 'The message.' } }, ['sessionId', 'text']) }),
  routed({ name: 'session.claim', title: 'Take over a session', description: 'Claim a session so its questions come to the person who asked, not to Autopilot.', tier: 'operate', module: 'session-control', route: { method: 'POST', path: '/api/sessions/:sessionId/claim', body: { confirm: true } }, input: obj({ sessionId: id('The session id.') }, ['sessionId']) }),
  routed({ name: 'session.release', title: 'Hand a session back', description: 'Release a claimed session back to Autopilot.', tier: 'operate', module: 'session-control', route: { method: 'DELETE', path: '/api/sessions/:sessionId/claim' }, input: obj({ sessionId: id('The session id.') }, ['sessionId']) }),
  routed({ name: 'mission.stop', title: 'Stop a mission', description: 'Stop a running mission and its sessions. Its record and history stay.', tier: 'operate', module: 'missions', destructive: true, route: { method: 'POST', path: '/api/missions/:missionId/stop' }, input: obj({ missionId: id('The mission id (msn_...).') }, ['missionId']) }),
  routed({ name: 'mission.pause', title: 'Pause a mission', description: 'Pause a mission: nothing new starts until it is resumed.', tier: 'operate', module: 'missions', route: { method: 'POST', path: '/api/missions/:missionId/pause' }, input: obj({ missionId: id('The mission id (msn_...).') }, ['missionId']) }),
  routed({ name: 'mission.resume', title: 'Resume a mission', description: 'Resume a paused mission where it stopped.', tier: 'operate', module: 'missions', route: { method: 'POST', path: '/api/missions/:missionId/resume' }, input: obj({ missionId: id('The mission id (msn_...).') }, ['missionId']) }),
  routed({ name: 'cloud.vm.start', title: 'Start a cloud VM', description: 'Start a stopped cloud VM; its machine comes online in about a minute.', tier: 'operate', module: 'cloud', route: { method: 'POST', path: '/api/cloud/vms/:vmId/start' }, input: obj({ vmId: id('The VM id, from cloud.vms.list.') }, ['vmId']) }),
  routed({ name: 'cloud.vm.stop', title: 'Stop a cloud VM', description: 'Stop a cloud VM after its running work finishes (up to waitMinutes). Its disk stays; start it again any time.', tier: 'operate', module: 'cloud', destructive: true, route: { method: 'POST', path: '/api/cloud/vms/:vmId/stop' }, input: obj({ vmId: id('The VM id, from cloud.vms.list.'), waitMinutes: { type: 'number', minimum: 0, maximum: 120, description: 'How long running work may finish first (0: stop now).' } }, ['vmId']) }),
  routed({ name: 'cloud.vm.restart', title: 'Restart a cloud VM', description: 'Reboot a running cloud VM; its machine is back in about a minute.', tier: 'operate', module: 'cloud', route: { method: 'POST', path: '/api/cloud/vms/:vmId/restart' }, input: obj({ vmId: id('The VM id, from cloud.vms.list.') }, ['vmId']) }),
  routed({ name: 'machine.drain', title: 'Stop new work on a machine', description: 'No new sessions or missions start on the machine; running work goes on.', tier: 'operate', module: 'scheduler', route: { method: 'POST', path: '/api/scheduler/nodes/:machineId/drain' }, input: obj({ machineId: id('The machine, by id or name.'), reason }, ['machineId']) }),
  routed({ name: 'machine.undrain', title: 'Take new work on a machine again', description: 'The machine takes new sessions and missions again.', tier: 'operate', module: 'scheduler', route: { method: 'POST', path: '/api/scheduler/nodes/:machineId/resume' }, input: obj({ machineId: id('The machine, by id or name.') }, ['machineId']) }),
  routed({ name: 'landing.cancel', title: 'Cancel a landing', description: 'Take a change out of the landing queue. The branch stays.', tier: 'operate', module: 'landing', destructive: true, route: { method: 'POST', path: '/api/landing/landings/:landingId/cancel' }, input: obj({ landingId: id('The landing id, from landing.list.') }, ['landingId']) }),
  routed({ name: 'landing.retry', title: 'Retry a landing', description: 'Run a failed landing again from the start.', tier: 'operate', module: 'landing', route: { method: 'POST', path: '/api/landing/landings/:landingId/retry' }, input: obj({ landingId: id('The landing id, from landing.list.') }, ['landingId']) }),
  routed({ name: 'watch.ack', title: 'Acknowledge an incident', description: 'Say someone has seen an incident, so it stops escalating.', tier: 'operate', module: 'watch', route: { method: 'POST', path: '/api/watch/:incidentId/ack' }, input: obj({ incidentId: id('The incident id, from watch.list.') }, ['incidentId']) }),
  routed({ name: 'aijob.cancel', title: 'Cancel an AI job', description: 'Stop a queued or running AI job.', tier: 'operate', module: 'aijobs', destructive: true, route: { method: 'POST', path: '/api/aijobs/:jobId/cancel' }, input: obj({ jobId: id('The AI job id, from aijobs.list.') }, ['jobId']) }),
  // Changes that cannot be undone.
  routed({ name: 'cloud.vm.delete', title: 'Delete a cloud VM', risk: 'Deletes the VM and its disk at the provider.', description: 'Delete a cloud VM and its disk after its running work finishes. Its machine leaves Orgus.', tier: 'elevated', module: 'cloud', destructive: true, route: { method: 'DELETE', path: '/api/cloud/vms/:vmId' }, input: obj({ vmId: id('The VM id, from cloud.vms.list.') }, ['vmId']) }),
];

/**
 * Everyday console routes ('METHOD /path', ':' a path segment) that api.call runs at the operate
 * tier rather than elevated (or critical, under a critical prefix): these and the routes of the
 * operate capabilities above. Each is named exactly, never a prefix.
 */
export const API_OPERATE_ROUTES: readonly string[] = [
  'POST /api/missions',
  'PATCH /api/missions/:',
  'POST /api/missions/:/start',
  'POST /api/missions/:/continue-fresh',
  'POST /api/missions/items',
  'PATCH /api/missions/items/:',
  'POST /api/missions/items/:/criteria/:',
  'POST /api/missions/links',
  'POST /api/missions/views',
  'POST /api/missions/signals/:/accept',
  'POST /api/missions/signals/:/dismiss',
  'POST /api/asks/:/dismiss',
  'POST /api/asks/:/snooze',
  'POST /api/asks/:/undo',
  'POST /api/asks/dismiss',
  'POST /api/watch/:/fixing',
  'POST /api/watch/:/fixed',
  'POST /api/watch/:/dismiss',
  'POST /api/notify/seen',
  'POST /api/notify/escalations/:/ack',
  'POST /api/notify/subscriptions',
  'POST /api/teams/claim',
  'POST /api/teams/assign',
  'POST /api/teams/assignments/:/ack',
  'POST /api/presence',
  'POST /api/scheduler/queue',
  'POST /api/scheduler/queue/:/retry',
  'POST /api/templates/:/start',
  'POST /api/comments',
  'PATCH /api/comments/:',
  'POST /api/landing/landings',
  'POST /api/approvals/:/dismiss',
];

const routeRe = (pattern: string): RegExp => new RegExp(`^${pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\/:[A-Za-z]*/g, '/[^/]+')}$`);
const OPERATE_RES: readonly { method: string; re: RegExp }[] = [
  ...API_OPERATE_ROUTES.map((r) => {
    const [method, path] = r.split(' ') as [string, string];
    return { method, re: routeRe(path) };
  }),
  ...AI_ROUTED.filter((c) => c.tier === 'operate').map((c) => ({ method: c.route!.method, re: routeRe(c.route!.path) })),
];

/** The routed capability a method and path are, if any. */
export function routedCapabilityFor(method: string, rawPath: string): Capability | undefined {
  const pathname = rawPath.split('?')[0]!;
  return AI_ROUTED.find((c) => c.route!.method === method && routeRe(c.route!.path).test(pathname));
}

/** A routed capability's call: path parameters filled from the input, the rest as the query (GET) or the body. */
export function routeCall(cap: Capability, input: Record<string, unknown>): { method: string; path: string; body: unknown } {
  const r = cap.route!;
  const used = new Set<string>();
  const path = r.path.replace(/:([A-Za-z]+)/g, (_m, k: string) => {
    used.add(k);
    return encodeURIComponent(String(input[k] ?? ''));
  });
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) if (!used.has(k) && v !== undefined) rest[k] = v;
  if (r.method === 'GET') {
    const q = Object.entries(rest).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    return { method: 'GET', path: q ? `${path}?${q}` : path, body: undefined };
  }
  return { method: r.method, path, body: { ...(r.body ?? {}), ...rest } };
}

/**
 * Every feature module's API by method and path, for what the named capabilities do not
 * cover (flows, schedules, notifications, settings). Reads are read tier;
 * changes are elevated, critical on the paths in API_CRITICAL_PREFIXES. Paths in
 * API_BLOCKED_PREFIXES are never reachable this way (the AI API itself, and the assistant's
 * own settings and approvals, so an agent cannot widen what it may do).
 */
export const AI_API: readonly Capability[] = [
  {
    name: 'api.get',
    title: 'Read any module API',
    description: 'GET a feature module route, for example /api/missions, /api/flows, /api/scheduler, /api/costs, /api/notify/settings. Use the named read tools first; this reaches the rest. Answers with the JSON the route returns.',
    tier: 'read',
    kind: 'api',
    input: obj({ path: { type: 'string', minLength: 5, maxLength: 1000, description: 'The path with its query string, starting /api/.' } }, ['path']),
  },
  {
    name: 'api.call',
    risk: 'Reaches every module route that has no named capability.',
    title: 'Change through any module API',
    description: 'POST, PATCH, PUT or DELETE a feature module route with a JSON body, for example changing a schedule or a flow. Use the item and proposal tools for the backlog. Elevated; critical for policy, secrets, sign-in, teams, machines, fleet, terminals and session control.',
    tier: 'elevated',
    kind: 'api',
    destructive: true,
    input: obj(
      {
        method: { type: 'string', enum: ['POST', 'PATCH', 'PUT', 'DELETE'], description: 'The HTTP method.' },
        path: { type: 'string', minLength: 5, maxLength: 1000, description: 'The path, starting /api/.' },
        body: { description: 'The JSON body, when the route takes one.' },
      },
      ['method', 'path'],
    ),
  },
];

/**
 * The layered catalogue (R-FN-5): a tiny index of areas, then one area's routes and settings on
 * demand, so an agent finds any feature without the whole API in its context. settings.set runs
 * at the tier of the route that writes the setting (apiCallTier), under the same gate and
 * approval rules as api.call. catalog.ts answers them on both hosts.
 */
export const AI_CATALOG: readonly Capability[] = [
  {
    name: 'catalog.index',
    title: 'List feature areas',
    description: 'Every feature area of Orgus in one line each. Then catalog.describe an area for its routes (for api.get and api.call) and settings (for settings.get and settings.set).',
    tier: 'read',
    kind: 'read',
    input: obj({}),
  },
  {
    name: 'catalog.describe',
    title: 'Describe a feature area',
    description: "One area's routes (method, path, tier, least role, whether a person must sign off, and the named capability when there is one) and settings (type, range, default and current value).",
    tier: 'read',
    kind: 'read',
    input: obj({ area: { type: 'string', minLength: 2, maxLength: 60, description: 'The area name from catalog.index.' } }, ['area']),
  },
  {
    name: 'settings.get',
    title: 'Read a setting',
    description: "A setting's current value, default and range, by its key from catalog.describe (e.g. scheduler.headroom.maxLoad).",
    tier: 'read',
    kind: 'read',
    input: obj({ key: { type: 'string', minLength: 3, maxLength: 200, description: 'The setting key.' } }, ['key']),
  },
  {
    name: 'settings.set',
    title: 'Change a setting',
    risk: 'Changes how Orgus runs for everyone in the workspace.',
    description: 'Change one setting by its key from catalog.describe. The value is checked against its type and range first; the module checks roles and audits the change. Runs at the tier of the route that writes it.',
    tier: 'elevated',
    // Like api.call: it writes through whichever module owns the setting's route, so it names
    // no module of its own. catalog.ts answers it by name before any module dispatch.
    kind: 'api',
    input: obj({ key: { type: 'string', minLength: 3, maxLength: 200, description: 'The setting key.' }, value: { description: 'The new value (null where the setting allows it).' } }, ['key', 'value']),
  },
];

/** The tier a settings.set of this key needs: its write route's, as api.call would run it. */
export function settingWriteTier(key: unknown): CapabilityTier {
  const s = typeof key === 'string' ? settingByKey(key) : undefined;
  return s ? apiCallTier(s.write.path, s.write.method) : 'elevated';
}

/** Paths api.get and api.call never reach. */
export const API_BLOCKED_PREFIXES: readonly string[] = ['/api/ai', '/api/assistant'];
/** Sign-in flows and the live stream: a person's browser, never an agent (each answers a redirect, a cookie or a stream). */
export const API_PERSON_PREFIXES: readonly string[] = ['/api/session', '/api/stream', '/api/auth/github', '/api/auth/device', '/api/auth/sign-out', '/api/auth/sessions', '/api/auth/identities', '/api/auth/invites/peek', '/api/auth/providers', '/api/health', '/api/logs'];
/** Paths where api.call is critical: as strong as a shell, or a change to who may do what. */
export const API_CRITICAL_PREFIXES: readonly string[] = [
  '/api/auth',
  '/api/teams',
  '/api/policy',
  '/api/policies',
  '/api/secrets',
  '/api/fleet',
  '/api/control',
  '/api/trust',
  '/api/security',
  '/api/term',
  '/api/join-tokens',
  '/api/machines',
  '/api/session-control',
  '/api/pair',
  '/api/launch-accounts',
];

const underPrefix = (pathname: string, prefix: string): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}?`);

/** Why a path cannot be used by api.get or api.call, or null when it can. */
export function apiPathError(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.startsWith('/api/')) return 'The path must start with /api/.';
  if (/\.\.|\\|%2e|%2f|%5c/i.test(raw)) return 'The path may not contain .., backslashes or encoded separators.';
  const pathname = raw.split('?')[0]!;
  if (API_BLOCKED_PREFIXES.some((p) => underPrefix(pathname, p))) return `${pathname} is not reachable through the AI API: agents cannot change their own permissions.`;
  return null;
}

/**
 * The tier of an api.call by its path and method: critical under API_CRITICAL_PREFIXES, operate
 * for the everyday routes (API_OPERATE_ROUTES and the operate routed capabilities), else elevated.
 */
export function apiCallTier(rawPath: unknown, method?: unknown): CapabilityTier {
  const pathname = typeof rawPath === 'string' ? rawPath.split('?')[0]! : '';
  // An everyday route is named exactly, so it wins over its prefix (claiming work under /api/teams).
  if (typeof method === 'string' && OPERATE_RES.some((r) => r.method === method && r.re.test(pathname))) return 'operate';
  return API_CRITICAL_PREFIXES.some((p) => underPrefix(pathname, p)) ? 'critical' : 'elevated';
}

/**
 * How an agent (the assistant) does what a console route does:
 *   named    a capability by name (the tier is the capability's)
 *   api      api.get or api.call by method and path (the tier from apiCallTier)
 *   person   critical: an agent reaches it only with a person's sign-off on that exact call
 *   browser  a sign-in flow or the live stream: a person's browser, never an agent
 *   blocked  the AI API and the assistant's own settings: agents cannot widen what they may do
 */
export type AiCoverageHow = 'named' | 'api' | 'person' | 'browser' | 'blocked';

export interface AiRouteCoverage {
  how: AiCoverageHow;
  capability: string;
  tier: CapabilityTier | null;
}

/** Core routes a named capability already covers. */
const CORE_ALIASES: readonly [string, string][] = [
  ['GET /api/state', 'state.summary'],
  ['GET /api/audit', 'audit.list'],
  ['POST /api/actions', 'session.start'],
  ['POST /api/approvals/:', 'approval.approve'],
  ['POST /api/missions/items', 'item.create'],
  ['PATCH /api/missions/items/:', 'item.update'],
  ['POST /api/missions/links', 'item.link'],
  ['POST /api/missions/import', 'item.import'],
  ['POST /api/missions/proposals/:/apply', 'proposal.accept'],
  ['POST /api/missions/proposals/:/discard', 'proposal.discard'],
  ['POST /api/missions', 'mission.start'],
];

/** How an agent reaches a console route (method, and path with ':' segments or real ids). */
export function aiRouteCoverage(method: string, rawPath: string): AiRouteCoverage {
  const pathname = rawPath.split('?')[0]!;
  if (API_BLOCKED_PREFIXES.some((p) => underPrefix(pathname, p))) return { how: 'blocked', capability: '-', tier: null };
  if (API_PERSON_PREFIXES.some((p) => underPrefix(pathname, p)) || /^\/api\/auth\/[^/]+\/start$/.test(pathname)) return { how: 'browser', capability: '-', tier: null };
  const routed = routedCapabilityFor(method, pathname);
  if (routed) return { how: 'named', capability: routed.name, tier: routed.tier };
  const alias = CORE_ALIASES.find(([r]) => {
    const [m, path] = r.split(' ') as [string, string];
    return m === method && routeRe(path).test(pathname);
  });
  if (alias) return { how: 'named', capability: alias[1], tier: capabilityByName(alias[1])!.tier };
  if (method === 'GET' || method === 'HEAD') return { how: 'api', capability: 'api.get', tier: 'read' };
  const tier = apiCallTier(pathname, method);
  return { how: tier === 'critical' ? 'person' : 'api', capability: 'api.call', tier };
}

/** The whole catalogue: reads first, then actions, then the module API. */
export const AI_CAPABILITIES: readonly Capability[] = [...AI_READS, ...AI_ACTIONS, ...AI_ROUTED, ...AI_CATALOG, ...AI_API];

export function capabilityByName(name: string): Capability | undefined {
  return AI_CAPABILITIES.find((c) => c.name === name);
}

/** MCP tool names allow [A-Za-z0-9_-]: 'session.stop' becomes 'session_stop'. */
export function mcpToolName(capability: string): string {
  return capability.replace(/\./g, '_');
}

export function capabilityFromToolName(tool: string): Capability | undefined {
  return AI_CAPABILITIES.find((c) => mcpToolName(c.name) === tool);
}

/** The tier this call needs: the capability's tier, raised by any escalation the input meets. */
export function effectiveTier(cap: Capability, input: Record<string, unknown> = {}): CapabilityTier {
  let tier = cap.tier;
  if (cap.name === 'api.call') tier = apiCallTier(input.path, input.method);
  if (cap.name === 'settings.set') tier = settingWriteTier(input.key);
  for (const e of cap.escalations ?? []) {
    const v = input[e.field];
    if ((typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && e.values.includes(v)) tier = maxTier(tier, e.tier);
  }
  return tier;
}

// ---------------------------------------------------------------------------
// Who may do what
// ---------------------------------------------------------------------------

export type AiRole = 'owner' | 'admin' | 'operator' | 'viewer';

/**
 * The caller as the gate sees it. The auth module's bearer-token actor may carry `tokenId`
 * and `scopes` (ApiTokenInfo.scopes) beside the Actor fields; both are optional.
 */
export interface AiCaller {
  id: string;
  kind: 'human' | 'agent' | 'system';
  role: AiRole;
  tokenId?: string;
  /**
   * Tier names ('read', 'operate', 'elevated'), capability names, and the auth module's broad
   * scopes ('actions', 'write'). Empty or absent: what the role allows.
   */
  scopes?: readonly string[];
  /**
   * A person's sign-off on one exact call (they approved it, or the owner turned on full
   * autonomy for the native assistant). Set only in the hub's own process, never from a
   * token. It lets an agent reach critical for that capability, still under the role ceiling.
   */
  intent?: AiIntent;
}

export interface AiIntent {
  capability: string;
  /** Who signed off: the approver's actor id, or the owner who chose full autonomy. */
  by: string;
  how: 'approved' | 'autonomy';
}

/** The highest tier each role reaches. */
export const ROLE_CEILING: Record<AiRole, CapabilityTier> = {
  viewer: 'read',
  operator: 'operate',
  admin: 'elevated',
  owner: 'critical',
};

/** Agents reach this without a scope that says more. */
export const AGENT_DEFAULT_CEILING: CapabilityTier = 'operate';

export interface AiDecision {
  allowed: boolean;
  /** Why not, in one sentence a person can act on. */
  why?: string;
}

/**
 * The auth module's broad token scopes (TOKEN_SCOPES in ./auth.ts) as the gate reads them:
 * 'actions' covers action capabilities and 'write' module ones (approvals, missions), as
 * they do on POST /api/actions and the module routes; for an agent only up to the operate
 * tier, because elevated has to be named.
 */
function broadScopeCovers(scopes: readonly string[], cap: Capability, tier: CapabilityTier, who: AiCaller): boolean {
  const broad = cap.kind === 'action' ? 'actions' : cap.kind === 'module' || cap.kind === 'api' ? 'write' : null;
  if (!broad || !scopes.includes(broad)) return false;
  return who.kind !== 'agent' || tierRank(tier) <= tierRank(AGENT_DEFAULT_CEILING);
}

/**
 * May this caller use this capability at this tier? Scopes only ever narrow what the role
 * allows; agents reach critical only with a person's sign-off on that capability (`intent`);
 * agents reach elevated only when a scope names the capability or the tier.
 */
export function decideCapability(cap: Capability, tier: CapabilityTier, who: AiCaller): AiDecision {
  if (tier === 'critical' && who.kind === 'agent' && who.intent?.capability !== cap.name) {
    return { allowed: false, why: `${cap.title} is a critical capability, and only a person may use those.` };
  }
  const roleCeiling = ROLE_CEILING[who.role] ?? 'read';
  if (tierRank(tier) > tierRank(roleCeiling)) {
    return { allowed: false, why: `${cap.title} needs the ${TIER_LABEL[tier].toLowerCase()} tier, and the ${who.role} role reaches ${TIER_LABEL[roleCeiling].toLowerCase()}.` };
  }
  const scopes = who.scopes ?? [];
  if (scopes.length) {
    const ok = scopes.includes(cap.name) || scopes.some((s) => isCapabilityTier(s) && tierRank(s) >= tierRank(tier)) || broadScopeCovers(scopes, cap, tier, who);
    if (!ok) return { allowed: false, why: `This token's scopes (${scopes.join(', ')}) do not include ${cap.name} or the ${tier} tier.` };
    return { allowed: true };
  }
  if (who.kind === 'agent' && tierRank(tier) > tierRank(AGENT_DEFAULT_CEILING)) {
    return { allowed: false, why: `${cap.title} needs the ${tier} tier. Agent tokens reach it only when their scopes name ${cap.name} or '${tier}'.` };
  }
  return { allowed: true };
}

const minTier = (a: CapabilityTier, b: CapabilityTier): CapabilityTier => (tierRank(a) <= tierRank(b) ? a : b);

/**
 * An API token's scopes (auth's vocabulary: read, write, actions, admin, or action kinds) as
 * the AI gate reads them (capability tiers and names). Empty stays empty: no narrowing.
 * The AI gate is the first check only: an action still passes auth's own scope check, and a
 * module capability is forwarded with the caller's credentials to its route.
 *   read                        the read tier
 *   write, admin                up to elevated (approvals, missions); agents up to operate
 *   actions                     every tier the role reaches; agents up to operate
 *   an action kind              that capability
 *   a tier or capability name   kept as it is
 */
export function aiScopesOf(kind: AiCaller['kind'], scopes: readonly string[]): string[] {
  const out = new Set<string>();
  const agentCap = (t: CapabilityTier): CapabilityTier => (kind === 'agent' ? minTier(t, AGENT_DEFAULT_CEILING) : t);
  for (const s of scopes) {
    if (s === 'read') out.add('read');
    else if (s === 'write' || s === 'admin') out.add(agentCap('elevated'));
    else if (s === 'actions') out.add(agentCap('critical'));
    else if (isCapabilityTier(s) || capabilityByName(s)) out.add(s);
    else {
      const c = AI_ACTIONS.find((x) => x.action === s);
      if (c) out.add(c.name);
    }
  }
  // A token whose scopes name nothing the gate knows reaches only reads, never everything.
  if (scopes.length && !out.size) out.add('read');
  return [...out];
}

/** The highest tier a token of this kind and role can ever use: agents stop below critical. */
export function tokenCeiling(role: AiRole, kind: 'human' | 'agent'): CapabilityTier {
  const ceiling = ROLE_CEILING[role] ?? 'read';
  return kind === 'agent' ? minTier(ceiling, 'elevated') : ceiling;
}

/** The capabilities a token of this kind and role can be given by name, in catalogue order. */
export function grantableCapabilities(role: AiRole, kind: 'human' | 'agent'): Capability[] {
  const top = tierRank(tokenCeiling(role, kind));
  return AI_CAPABILITIES.filter((c) => tierRank(c.tier) <= top);
}

/**
 * Why a token scope names more than a token of this kind and role may ever use, or null.
 * The gate would refuse it anyway (scopes only narrow); refusing it when the token is made
 * means nobody believes they gave an agent something it does not have.
 */
export function scopeBeyondRole(scope: string, role: AiRole, kind: 'human' | 'agent'): string | null {
  const tier = isCapabilityTier(scope) ? scope : capabilityByName(scope)?.tier;
  if (!tier || tierRank(tier) <= tierRank(tokenCeiling(role, kind))) return null;
  if (kind === 'agent' && tier === 'critical') return `${scope} is critical, and only a person may use critical capabilities.`;
  return `${scope} needs the ${tier} tier, and the ${role} role reaches ${tokenCeiling(role, kind)}.`;
}

/**
 * The highest whole tier this caller reaches (for display). Capabilities a scope names
 * one by one may go above it, up to the role's ceiling.
 */
export function callerCeiling(who: AiCaller): CapabilityTier {
  let ceiling = ROLE_CEILING[who.role] ?? 'read';
  const scopes = who.scopes ?? [];
  if (scopes.length) {
    const tiers: CapabilityTier[] = scopes.filter(isCapabilityTier);
    // 'actions' or 'write' (the auth module's broad scopes) reach operate as a whole.
    if (scopes.includes('actions') || scopes.includes('write')) tiers.push('operate');
    ceiling = minTier(ceiling, tiers.length ? tiers.reduce(maxTier) : 'read');
  } else if (who.kind === 'agent') {
    ceiling = minTier(ceiling, AGENT_DEFAULT_CEILING);
  }
  return who.kind === 'agent' ? minTier(ceiling, 'elevated') : ceiling;
}

/** The capability an action kind maps to (every ActionKind has one). */
export function capabilityForAction(kind: ActionKind): Capability | undefined {
  return AI_ACTIONS.find((c) => c.action === kind);
}

// ---------------------------------------------------------------------------
// HTTP API
// ---------------------------------------------------------------------------

/** One catalogue entry as GET /api/ai/capabilities reports it for the caller. */
export interface CapabilityGrant extends Capability {
  allowed: boolean;
  why?: string;
}

/** GET /api/ai/capabilities */
export interface AiCapabilitiesResponse {
  version: string;
  caller: { id: string; kind: AiCaller['kind']; role: AiRole; ceiling: CapabilityTier; scopes: string[] };
  capabilities: CapabilityGrant[];
  limits: AiRateLimits;
}

/** POST /api/ai/invoke */
export interface AiInvokeRequest {
  capability: string;
  input?: Record<string, unknown>;
}

/** The answer to POST /api/ai/invoke when it ran. Failures answer { error: UiErrorShape }. */
export interface AiInvokeResponse {
  ok: boolean;
  capability: string;
  tier: CapabilityTier;
  /** What the read returned, or the action's result data. */
  data?: unknown;
  message?: string;
  /** Audit entry for actions. */
  auditId?: Id;
}

/** GET /api/ai/connect: how to connect an MCP client to this hub. */
export interface AiConnectInfo {
  hubUrl: string;
  /** The stdio command an MCP client runs. */
  command: string;
  args: string[];
  /** A ready-to-paste .mcp.json / Claude Code config, with a placeholder for the token. */
  mcpJson: string;
  /** `claude mcp add ...` with a placeholder for the token. */
  claudeCommand: string;
  tokenPlaceholder: string;
}

// ---------------------------------------------------------------------------
// Rate limits and activity (state.ext.ai)
// ---------------------------------------------------------------------------

export interface AiRateLimits {
  /** Per agent, per minute. Bursts up to the same number. */
  readsPerMinute: number;
  actionsPerMinute: number;
}

export const AI_DEFAULT_LIMITS: AiRateLimits = { readsPerMinute: 120, actionsPerMinute: 12 };

/**
 * ok: ran. error: ran and failed (or the machine refused). refused: over the caller's tier.
 * limited: over the rate limit. invalid: the input did not match the schema.
 */
export type AiOutcome = 'ok' | 'error' | 'refused' | 'limited' | 'invalid';

export interface AiActivity {
  id: Id;
  at: Ts;
  /** Actor id, e.g. 'agent:ops'. */
  actor: string;
  actorKind: AiCaller['kind'];
  capability: string;
  tier: CapabilityTier;
  outcome: AiOutcome;
  /** One line: what it touched or why it did not run. */
  message?: string;
  auditId?: Id;
  /** How long it took. */
  ms: number;
  /** Came straight through POST /api/actions rather than /api/ai/invoke. */
  direct?: boolean;
}

export interface AiAgentSummary {
  /** Actor id, e.g. 'agent:ops'. */
  id: string;
  name: string;
  tokenId: string | null;
  role: AiRole;
  ceiling: CapabilityTier;
  firstSeenAt: Ts;
  lastSeenAt: Ts;
  lastActionAt: Ts | null;
  /** Since local midnight on the hub. */
  readsToday: number;
  actionsToday: number;
  refusedToday: number;
}

/** state.ext.ai */
export interface AiState {
  /** Agents that called the hub, most recently seen first. */
  agents: AiAgentSummary[];
  /** Newest first, last 100 calls through the AI API (agents and people). */
  recent: AiActivity[];
  limits: AiRateLimits;
  version: string;
}

// ---------------------------------------------------------------------------
// Hub <-> agent module messages (ExtMessage with ext 'ai')
// ---------------------------------------------------------------------------

/** hub -> agent, type 'transcript' (with an id): the latest transcript entries of a session. */
export interface AiTranscriptRequest {
  sessionId: Id;
  limit: number;
  /** Keep the end of long entries, with their line breaks (a final report sits at the end). */
  tail?: boolean;
}

export interface AiTranscriptEntry {
  at: Ts | null;
  /** prompt, say, tool, result, error, system (as the agent's timeline names them). */
  kind: string;
  text: string;
  tool?: string;
}

/** agent -> hub, type 'transcript.result' (same id). */
export interface AiTranscriptResult {
  sessionId: Id;
  known: boolean;
  entries: AiTranscriptEntry[];
  /** More entries exist before the first one returned. */
  truncated: boolean;
}
